import React from "react";
import { Box, Typography, Stack, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";

const StyledCard = styled(motion.div)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.03)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  padding: "20px 24px",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  minHeight: "90px",
  display: "grid",
  flexDirection: "row",
  gap: "16px",
  alignItems: "flex-start",
  width: "100%",
  flex: 1,
  "&:hover": {
    background: "rgba(255, 255, 255, 0.06)",
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    gap: "12px",
    minHeight: "auto",
    flex: "none",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: "49px",
  height: "49px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: "rgba(255, 255, 255, 0.08)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  [theme.breakpoints.down("md")]: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
  },
}));

const FeatureCard = ({ icon, title, description, details }) => {
  const IconComponent = LucideIcons[icon];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const iconSize = isMobile ? 18 : 22;

  return (
    <StyledCard
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5 }}
    >
      <IconContainer sx={{ alignSelf: "flex-start", mt: "2px" }}>
        {IconComponent && <IconComponent size={iconSize} color="#A1B0C1" strokeWidth={2} />}
      </IconContainer>
      
      <Stack spacing={{ xs: 0.8, md: 1 }} flex={1}>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: { xs: "14px", md: "16px" },
            fontFamily: "'Poppins', sans-serif",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: { xs: "12px", md: "13px" },
            lineHeight: 1.5,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {description}
        </Typography>
        
        {details && (
          <Stack spacing={{ xs: 0.3, md: 0.5 }} sx={{ mt: { xs: 0.5, md: 1 } }}>
            {details.map((detail, index) => (
              <Typography
                key={index}
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: { xs: "11px", md: "12px" },
                  lineHeight: 1.4,
                  fontFamily: "'Poppins', sans-serif",
                  "&::before": {
                    content: '"• "',
                    color: "#4674D9",
                    fontWeight: "bold",
                  }
                }}
              >
                {detail}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    </StyledCard>
  );
};

export default FeatureCard;